'use client';

import { useEffect, useRef } from 'react';

interface ScrollTranslateProps {
  children: React.ReactNode;
  className?: string;
  translateX?: number;
  translateY?: number;
}

export default function ScrollTranslate({
  children,
  className = '',
  translateX = 0,
  translateY = 0
}: ScrollTranslateProps) {
  const ref = useRef<HTMLDivElement>(null);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const updateTransform = () => {
      const rect = el.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Calculate scroll progress centered around viewport middle
      const viewportCenter = windowHeight / 2;
      const elementCenter = rect.top + rect.height / 2;
      const normalizedDistance = (elementCenter - viewportCenter) / (windowHeight / 2);

      // Clamp and apply easing
      const clamped = Math.max(-1, Math.min(1, normalizedDistance));
      const eased = clamped * (2 - Math.abs(clamped));

      // Write directly to DOM — no React re-render, no forced reflow
      el.style.transform = `translate3d(${translateX * -eased}px, ${translateY * -eased}px, 0)`;
    };

    const handleScroll = () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      rafId.current = requestAnimationFrame(updateTransform);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    updateTransform();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [translateX, translateY]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ willChange: 'transform' }}
    >
      {children}
    </div>
  );
}

